import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Pencil, Eraser, Trash2, Undo2, X, Minus, Plus } from 'lucide-react';
import { socketService } from '../services/socketService';

interface WhiteboardProps {
  callId: string;
  onClose: () => void;
  /** 참가 시점의 기록 — 서버에서 다시 받지 못했을 때만 쓴다 */
  initialHistory?: DrawAction[];
}

interface DrawPoint {
  x: number; // 0~1 비율
  y: number;
}

interface DrawAction {
  tool: 'pen' | 'eraser';
  color: string;
  size: number;
  points: DrawPoint[];
}

interface DrawEvent {
  callId: string;
  action: DrawAction;
}

const COLORS = ['#ffffff', '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];
const SIZES = [2, 4, 8, 12];

const Whiteboard: React.FC<WhiteboardProps> = ({ callId, onClose, initialHistory }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tool, setTool] = useState<'pen' | 'eraser'>('pen');
  const [color, setColor] = useState('#ffffff');
  const [brushSize, setBrushSize] = useState(4);
  const [isDrawing, setIsDrawing] = useState(false);
  const currentPoints = useRef<DrawPoint[]>([]);
  const history = useRef<DrawAction[]>([]);
  const [historyLen, setHistoryLen] = useState(0);

  // 캔버스 크기 조정
  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      // 기존 내용 보존
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      tempCanvas.getContext('2d')?.drawImage(canvas, 0, 0);

      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;

      // 기존 내용 복원 (비율 조정)
      const ctx = canvas.getContext('2d');
      if (ctx && tempCanvas.width > 0) {
        ctx.drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height, 0, 0, canvas.width, canvas.height);
      }
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // 소켓 이벤트 수신
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleDraw = (data: DrawEvent) => {
      if (data.callId !== callId) return;
      drawAction(data.action);
      history.current.push(data.action);
      setHistoryLen(history.current.length);
    };

    const handleClear = (data: { callId: string }) => {
      if (data.callId !== callId) return;
      clearCanvas();
      history.current = [];
      setHistoryLen(0);
    };

    socket.on('whiteboard:draw', handleDraw);
    socket.on('whiteboard:clear', handleClear);

    return () => {
      socket.off('whiteboard:draw', handleDraw);
      socket.off('whiteboard:clear', handleClear);
    };
  }, [callId]);

  // 캔버스에 드로잉 액션 적용
  const drawAction = useCallback((action: DrawAction) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx || action.points.length < 2) return;

    ctx.beginPath();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (action.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = action.size * 3;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = action.color;
      ctx.lineWidth = action.size;
    }

    const w = canvas.width;
    const h = canvas.height;
    ctx.moveTo(action.points[0].x * w, action.points[0].y * h);
    for (let i = 1; i < action.points.length; i++) {
      ctx.lineTo(action.points[i].x * w, action.points[i].y * h);
    }
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  // 열 때마다 서버가 들고 있는 그림을 받아 다시 그린다 — 늦게 들어온 사람, 닫았다 다시 연 사람도 지금까지 그린 것을 본다
  // (예전엔 화이트보드가 닫히면 언마운트돼 기록이 사라졌고, 늦게 들어온 사람에게는 처음부터 빈 판이었다).
  // 서버가 처리한 순서상 응답 전 받은 그림은 응답에 이미 들어 있으므로 기록을 응답으로 바꿔 끼운다
  const initialHistoryRef = useRef(initialHistory);
  useEffect(() => {
    let cancelled = false;
    socketService.emitCallWithAck('whiteboard:sync', { callId }, 5000).then(res => {
      if (cancelled) return;
      const actions = res.ok && Array.isArray(res.actions) ? res.actions : initialHistoryRef.current;
      if (!Array.isArray(actions) || actions.length === 0) return;
      clearCanvas();
      history.current = [...actions];
      history.current.forEach(action => drawAction(action));
      setHistoryLen(history.current.length);
    });
    return () => { cancelled = true; };
  }, [callId, clearCanvas, drawAction]);

  // 좌표를 0~1 비율로 변환
  const getPoint = (e: React.MouseEvent | React.TouchEvent): DrawPoint | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();

    let clientX: number, clientY: number;
    if ('touches' in e) {
      if (e.touches.length === 0) return null;
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height,
    };
  };

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    const point = getPoint(e);
    if (!point) return;
    setIsDrawing(true);
    currentPoints.current = [point];
  };

  const handlePointerMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const point = getPoint(e);
    if (!point) return;

    currentPoints.current.push(point);

    // 실시간으로 로컬 캔버스에 그리기
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pts = currentPoints.current;
    const prev = pts[pts.length - 2];
    const curr = pts[pts.length - 1];

    ctx.beginPath();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = brushSize * 3;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
      ctx.lineWidth = brushSize;
    }

    ctx.moveTo(prev.x * canvas.width, prev.y * canvas.height);
    ctx.lineTo(curr.x * canvas.width, curr.y * canvas.height);
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  };

  const handlePointerUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (currentPoints.current.length < 2) return;

    const action: DrawAction = {
      tool,
      color,
      size: brushSize,
      points: [...currentPoints.current],
    };

    history.current.push(action);
    setHistoryLen(history.current.length);

    // 소켓으로 전송
    const socket = socketService.getSocket();
    socket?.emit('whiteboard:draw', { callId, action } as DrawEvent);

    currentPoints.current = [];
  };

  // 되돌리기: 전체 다시 그리기
  const handleUndo = () => {
    if (history.current.length === 0) return;
    history.current.pop();
    setHistoryLen(history.current.length);
    clearCanvas();
    history.current.forEach(action => drawAction(action));
  };

  // 전체 지우기
  const handleClear = () => {
    clearCanvas();
    history.current = [];
    setHistoryLen(0);
    const socket = socketService.getSocket();
    socket?.emit('whiteboard:clear', { callId });
  };

  return (
    <div className="flex flex-col h-full bg-gray-900/80 backdrop-blur-sm">
      {/* 도구 바 — 펜 선택 시 ~650px라 좁은 화면(채팅 패널을 연 통화 화면 포함)에선 줄바꿈해야 닫기 버튼이 잘리지 않는다 */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-gray-800/90 border-b border-gray-700">
        {/* 펜/지우개 */}
        <button
          onClick={() => setTool('pen')}
          className={`p-2 rounded-lg transition-colors ${tool === 'pen' ? 'bg-brand-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
          title="펜"
        >
          <Pencil size={18} />
        </button>
        <button
          onClick={() => setTool('eraser')}
          className={`p-2 rounded-lg transition-colors ${tool === 'eraser' ? 'bg-brand-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
          title="지우개"
        >
          <Eraser size={18} />
        </button>

        <div className="w-px h-6 bg-gray-600 mx-1" />

        {/* 색상 팔레트 */}
        {tool === 'pen' && (
          <div className="flex items-center gap-1">
            {COLORS.map(c => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c ? 'border-white scale-110' : 'border-gray-600 hover:scale-105'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        )}

        {tool === 'pen' && <div className="w-px h-6 bg-gray-600 mx-1" />}

        {/* 굵기 */}
        <div className="flex items-center gap-1">
          {SIZES.map(s => (
            <button
              key={s}
              onClick={() => setBrushSize(s)}
              className={`flex items-center justify-center w-7 h-7 rounded-lg transition-colors ${brushSize === s ? 'bg-brand-600' : 'hover:bg-gray-700'}`}
              title={`굵기 ${s}`}
            >
              <div
                className="rounded-full bg-white"
                style={{ width: Math.min(s + 2, 14), height: Math.min(s + 2, 14) }}
              />
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-gray-600 mx-1" />

        {/* 되돌리기 / 전체 지우기 */}
        <button
          onClick={handleUndo}
          disabled={historyLen === 0}
          className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="되돌리기"
        >
          <Undo2 size={18} />
        </button>
        <button
          onClick={handleClear}
          disabled={historyLen === 0}
          className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="전체 지우기"
        >
          <Trash2 size={18} />
        </button>

        <div className="flex-1" />

        {/* 닫기 */}
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          title="화이트보드 닫기"
        >
          <X size={18} />
        </button>
      </div>

      {/* 캔버스 */}
      <div ref={containerRef} className="flex-1 cursor-crosshair">
        <canvas
          ref={canvasRef}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          className="block"
        />
      </div>
    </div>
  );
};

export default Whiteboard;
